import * as React from 'react';
import { isBootFeatureEnabled } from 'owa-metatags';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
    ReactBroadComponentType,
    LazyComponent,
    LazyModule,
    createLazyComponent,
    ErrorHandlerFunction,
    ErrorHandlerComponent,
} from 'owa-bundling';
import { LazyBootModule } from 'owa-bundling-light';
import { lazyGetApolloClient, lazyApolloProvider } from 'owa-apollo';

/**
 * VSO - 101850 - Remove createLazyApolloComponent once apollo is enabled as boot flight
 * This wraps the actual component (which is lazy initialized) with ApolloProvider
 */
export function createLazyApolloComponent<TProps, TModule, TInnerRef, TPlaceholderRef = void>(
    lazyModule: LazyModule<TModule>,
    getter: (module: TModule) => ReactBroadComponentType<TProps, TInnerRef>,
    placeholder?: ReactBroadComponentType<TProps, TPlaceholderRef>,
    importErrorHandler?: ErrorHandlerFunction | ErrorHandlerComponent
): LazyComponent<TProps, TInnerRef, TPlaceholderRef> {
    if (isBootFeatureEnabled('apollo')) {
        // When apollo is enabled at boot, the root component will be wrapped with the ApolloProvider
        // so we return immediately
        return createLazyComponent(lazyModule, getter, placeholder, importErrorHandler);
    } else {
        // When apollo is not enabled at boot, the root component will not be wrapped with the ApolloProvider
        // so if the scenario component wants to perform any apollo operations from a view component,
        // we wrap the view component with ApolloProvider. This is required so that apollo client instance
        // is available during operation execution.
        let client: ApolloClient<NormalizedCacheObject>;
        let ApolloProvider: React.FC<{ client: any }>;
        const innerModule = new LazyBootModule(async () => {
            const [resolvedClient, resolvedApolloProvider, resolvedModule] = await Promise.all([
                lazyGetApolloClient.importAndExecute(),
                lazyApolloProvider.import(),
                lazyModule.import(),
            ]);

            client = resolvedClient;
            ApolloProvider = resolvedApolloProvider;
            return resolvedModule;
        });

        const innerGetter = (m: TModule) => {
            const InnerComponent = getter(m);
            return props => (
                <ApolloProvider client={client}>
                    <InnerComponent {...props} />
                </ApolloProvider>
            );
        };

        return createLazyComponent<TProps, TModule, TInnerRef, TPlaceholderRef>(
            innerModule,
            innerGetter,
            placeholder,
            importErrorHandler
        );
    }
}
